import { useEffect, useRef } from "react";
import { useAuth } from "@/contexts/authContext";
import { buildWorkspaceEventProperties, identify } from "@/lib/metrics";

/**
 * Fires `identify()`, which also sets the workspace properties attached to
 * every event, whenever what it would send changes — at login, on a
 * workspace switch, and when a `/user/` refetch brings a new id, account,
 * role or workspace type — never per event, never per render. Keying on the
 * payload rather than the user or workspace is what lets a same-workspace
 * refetch reach Amplitude. Renders nothing; mount once inside the auth provider (same pattern as
 * LaunchDarklyIdentify). Logout clears identity via `resetMetrics()` in
 * authContext, and clearing the guard here lets a re-login re-identify.
 */
export function MetricsIdentify(): null {
  const { user, workspace, workspaceMember, analyticsIdentity } = useAuth();
  const lastIdentifiedKey = useRef<string | null>(null);

  useEffect(() => {
    if (!user) {
      lastIdentifiedKey.current = null;
      return;
    }
    if (!workspaceMember || !workspace) return;
    const userId = analyticsIdentity?.userId ?? user.id;
    const workspaceProperties = buildWorkspaceEventProperties(
      workspaceMember,
      workspace.subscriptionStatus,
      analyticsIdentity,
    );
    const accountId = analyticsIdentity?.accountId ?? null;
    const sharesOptroProfile = userId !== user.id;
    const key = JSON.stringify({ userId, workspaceProperties, accountId });
    if (lastIdentifiedKey.current === key) return;

    identify(userId, workspaceProperties, accountId, sharesOptroProfile);
    lastIdentifiedKey.current = key;
  }, [user, workspace, workspaceMember, analyticsIdentity]);

  return null;
}
